import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BlockProps, ListLayoutProps } from "@companion-core/shared/app/Interfaces/blocks";
import { ComponentData } from "@companion-core/shared/app/Interfaces/API";
import { useBlockItemComponents } from "@companion-core/shared/app/Queries";
import { ListLayout } from "@companion-core/shared/app/Components/Blocks/Layouts/ListLayout";
import { ExtendedMosaicLayout } from "@companion-core/shared/app/Components/Blocks/Layouts/ExtendedMosaicLayout";
import {
  Filter,
  FilterOption,
} from "@companion-core/shared/app/Components/Inputs/JPInputFilter/JPInputFilter.model";
import { useDynamicPageStore } from "@companion-core/shared/app/Store/dynamicPageStore";
import {
  getComponentsFromBlockItems,
  getComponentText,
} from "@companion-core/shared/app/Utils/componentHelper";
import { isWeb } from "@companion-core/shared/app/Utils/platform";
import { useFiltersInQueryParams } from "@companion-core/shared/app/Hooks/filters";
import { useFiltersStore } from "@companion-core/shared/app/Store/filters";
import { populateFilters } from "@companion-core/shared/app/Utils/filters";

const ListLayouts: {
  [key: string]: ({
    title,
    queryComponents,
    blockItemId,
    withSearchBar,
    withGeolocalisation,
  }: ListLayoutProps) => ReactElement | undefined;
} = {
  "list": ListLayout,
  "extended-mosaic": ExtendedMosaicLayout,
};

export const List = ({ simpleBlockItems, queryBlockItems, contentProps }: BlockProps) => {
  const { scrollIndex, pageSlug, setBlockItemComponents, setScrollIndex } = useDynamicPageStore(
    (state) => ({
      scrollIndex: state.scrollIndex,
      pageSlug: state.pageSlug,
      setScrollIndex: state.setScrollIndex,
      setBlockItemComponents: state.setBlockItemComponents,
    })
  );
  const { filters: savedFilters, search, resetFilters } = useFiltersStore();
  const { i18n } = useTranslation();
  isWeb() && useFiltersInQueryParams();
  const i18nLanguage: string = i18n?.languages[0]?.toLocaleLowerCase();
  const simpleComponents: ComponentData[] = useMemo(
    () => getComponentsFromBlockItems(simpleBlockItems),
    [simpleBlockItems]
  );
  const title: string = useMemo(
    () => getComponentText(simpleComponents)?.title ?? "",
    [simpleComponents]
  );
  const blockItemId: string = useMemo(() => queryBlockItems[0]?.id, [queryBlockItems]);
  const [filterParams, setFilterParams] = useState<Record<string, string> | undefined>();

  const {
    isFetchingNextPage,
    hasNextPage,
    data: queryComponentsData,
    isPending,
    fetchNextPage,
  } = useBlockItemComponents({
    id: blockItemId,
    lang: i18nLanguage,
    filterParams: filterParams,
  });

  const queryComponents: ComponentData[] = useMemo(() => {
    return (
      queryComponentsData?.pages.reduce<ComponentData[]>(
        (acc, { data }) => [...acc, ...(data?.results ?? [])],
        []
      ) ?? []
    );
  }, [queryComponentsData]);

  const initialFilters = useMemo(() => {
    const _filters = [...Object.entries(queryBlockItems[0]?.facets ?? {})];
    return _filters.map(([key, facet]) => ({
      name: key,
      options: facet as FilterOption[],
    }));
  }, []);

  const filters: Filter[] = [...initialFilters];
  const isFiltersEnabled: boolean = useMemo(() => !!filters?.length, [filters]);

  useEffect(() => {
    resetFilters();
    return () => resetFilters();
  }, []);

  useEffect(() => {
    setBlockItemComponents(contentProps.slug, blockItemId, queryComponents ?? []);
  }, [queryComponents]);

  useEffect(() => {
    setFilterParams({
      ...(search && { search }),
      ...(savedFilters && savedFilters),
    });
  }, [savedFilters, search]);

  useEffect(() => {
    if (isWeb()) {
      if (!isFetchingNextPage && pageSlug && scrollIndex[pageSlug]) {
        window.scrollTo(0, scrollIndex[pageSlug]);
      }

      if (pageSlug && scrollIndex[pageSlug] === window.scrollY) {
        setScrollIndex({ [pageSlug]: 0 });
      }

      const handleScroll = () => {
        const { scrollTop, clientHeight, scrollHeight: scrollH } = document.documentElement;
        if (scrollTop + clientHeight >= scrollH * 0.8 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage({ cancelRefetch: false });
        }
      };

      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [queryComponentsData, hasNextPage, fetchNextPage, setScrollIndex]);

  const { displayType } = contentProps;

  if (!ListLayouts[displayType]) {
    return <div>The component {displayType} has not been created yet.</div>;
  }

  const Layout = ListLayouts[displayType];

  const populatedFilters = useMemo(
    () => (savedFilters ? populateFilters(filters, savedFilters ?? {}) : initialFilters),
    [filters, savedFilters]
  );

  const withSearchBar = useMemo(
    () => queryBlockItems[0]?.query?.searchbar ?? false,
    [queryBlockItems]
  );

  const withGeolocalisation = useMemo(
    () => queryBlockItems[0]?.query?.geolocalisation ?? false,
    [queryBlockItems]
  );

  return (
    <Layout
      title={title}
      isPending={isPending}
      queryComponents={queryComponents}
      withFilters={isFiltersEnabled}
      blockItemId={blockItemId}
      filters={populatedFilters}
      withSearchBar={withSearchBar}
      withGeolocalisation={withGeolocalisation}
    />
  );
};
